import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useState,
} from "react";
import ModalComponent from "../modal";
import { TRAINING_TYPES, WEEK_DAYS } from "../../@data";
import { MenuItem, Select, TextField, Typography } from "@mui/material";
import Button from "../button";
import { useTraining } from "../../contexts/training";

export interface ITrainingModalMethods {
  handleOpen: (trainingToEdit?: {
    trainingType: string;
    description: string;
  }) => void;
}

const TrainingModal = forwardRef<ITrainingModalMethods>((_, ref) => {
  const { training, handleSaveTraining, saving } = useTraining();
  const [open, setOpen] = useState(false);
  const [selectedDay, setSelectedDay] = useState(new Date().getDay());
  const [trainingType, setTrainingType] = useState<string>("");
  const [description, setDescription] = useState<string>("");
  const [haveFocusInSomeInput, setHaveFocusInSomeInput] = useState(false);

  const handleCloseModal = useCallback(() => {
    if (!saving) {
      setOpen(false);
      setTimeout(() => {
        setTrainingType("");
        setDescription("");
      }, 500);
      setHaveFocusInSomeInput(false);
    }
  }, [saving]);

  const handleOpen: ITrainingModalMethods["handleOpen"] = useCallback(
    (trainingToEdit) => {
      setOpen(true);
      if (trainingToEdit) {
        setTrainingType(trainingToEdit.trainingType);
        setDescription(trainingToEdit.description);
      }
    },
    []
  );

  const handleChangeTrainingType = useCallback(
    (newType: string) => setTrainingType(newType),
    []
  );

  useEffect(() => {
    const daySelected = WEEK_DAYS[selectedDay];
    if (training && training?.[daySelected.key as never]?.[trainingType]) {
      setDescription(training[daySelected.key as never][trainingType]);
    } else setDescription("");
  }, [selectedDay, training, trainingType]);

  useImperativeHandle(ref, () => ({ handleOpen }));

  return (
    <ModalComponent
      title="Training modal component"
      open={open}
      handleClose={() => (!haveFocusInSomeInput ? handleCloseModal() : {})}
    >
      <Typography>Dia da semana</Typography>
      <Select
        size="small"
        sx={{ mt: 1, mb: 2 }}
        displayEmpty
        renderValue={(value: number) => {
          if (!Number.isNaN(value))
            return <Typography>{WEEK_DAYS[value].fullLabel}</Typography>;
          else return <Typography color="gray">Selecione o dia</Typography>;
        }}
        onChange={(e) => setSelectedDay(e.target.value as number)}
        value={selectedDay}
      >
        {WEEK_DAYS.map((day, i) => (
          <MenuItem key={`week-day-${day.key}`} value={i}>
            {day.fullLabel}
          </MenuItem>
        ))}
      </Select>
      <Typography>Tipo de exercício</Typography>
      <Select
        size="small"
        sx={{ mt: 1, mb: 2 }}
        displayEmpty
        renderValue={(value: string) => {
          if (value) return <Typography>{value}</Typography>;
          else
            return (
              <Typography color="gray">Selecione o grupo muscular</Typography>
            );
        }}
        onChange={(e) => handleChangeTrainingType(e.target.value)}
        value={trainingType}
      >
        {TRAINING_TYPES.map((type) => (
          <MenuItem key={`training-type-${type}`} value={type}>
            {type}
          </MenuItem>
        ))}
      </Select>
      <Typography>Descrição</Typography>
      <TextField
        inputProps={{
          placeholder: "Escreva os exercícios nesse espaço...",
        }}
        multiline
        size="small"
        fullWidth
        rows={4}
        sx={{ mt: 1, mb: 3 }}
        onChange={(e) => setDescription(e.target.value)}
        value={description}
        onFocus={() => setHaveFocusInSomeInput(true)}
        onBlur={() => {
          setTimeout(() => {
            setHaveFocusInSomeInput(false);
          }, 100);
        }}
      />
      <Button
        fullWidth
        variant="contained"
        size="large"
        onClick={() =>
          handleSaveTraining(
            WEEK_DAYS[selectedDay].key,
            trainingType,
            description,
            handleCloseModal
          )
        }
        loading={saving}
        disabled={!trainingType || !description.trim()}
        loadingText="Salvando..."
      >
        Salvar
      </Button>
      <Button
        sx={{ mt: 1 }}
        fullWidth
        variant="text"
        size="large"
        onClick={handleCloseModal}
        disabled={saving}
      >
        Voltar
      </Button>
    </ModalComponent>
  );
});

export default TrainingModal;
